import React from 'react';
import styled from 'styled-components';
import { ReactComponent as DisabledIcon } from '@elementary/common-ui/assets/16x16/disabled.svg';
import { ReactComponent as HexIcon } from '@elementary/common-ui/assets/40x40/hex-icon.svg';
import { ReactComponent as LightdashIcon } from '@elementary/common-ui/assets/40x40/lightdash-icon.svg';
import { ReactComponent as LookerIcon } from '@elementary/common-ui/assets/40x40/looker-icon.svg';
import { ReactComponent as PowerBiIcon } from '@elementary/common-ui/assets/40x40/powerbi-icon.svg';
import { ReactComponent as SigmaIcon } from '@elementary/common-ui/assets/40x40/sigma-icon.svg';
import { ReactComponent as TableauIcon } from '@elementary/common-ui/assets/40x40/tableau-icon.svg';
import { ReactComponent as ThoughtSpotIcon } from '@elementary/common-ui/assets/40x40/thoughtspot-icon.svg';
import { ReactComponent as Logo } from '@elementary/common-ui/assets/logo.svg';
import { ReactComponent as DeleteConnection } from '@elementary/common-ui/assets/minus-x-minus.svg';
import { Dialog, FlexCenter } from '@elementary/common-ui/components';
import { FetchBiIntegrationApi } from 'shared/types';
import { Typography } from '@mui/material';
import { fontWeight, radius, space } from '@elementary/common-ui/style/utils';

const platformIconMap: Record<string, React.ReactNode> = {
  Tableau: <TableauIcon />,
  Looker: <LookerIcon />,
  PowerBI: <PowerBiIcon />,
  Sigma: <SigmaIcon />,
  ThoughtSpot: <ThoughtSpotIcon />,
  Hex: <HexIcon />,
  Lightdash: <LightdashIcon />,
};

const IconsWrapper = styled(FlexCenter)`
  gap: ${space('sm')};
  height: 100%;
`;

const LogoWrapper = styled(FlexCenter)`
  width: 72px;
  height: 72px;
  border-radius: ${radius('xl')};
  padding: ${space('lg')};
  background: ${({ theme }) => theme.palette.accent.ai};
`;

const PlatformIconWrapper = styled(FlexCenter)`
  border-radius: ${radius('xl')};
  padding: ${space('lg')};
  border: 2px solid ${({ theme }) => theme.palette.border.default};
  box-sizing: border-box;
`;

const TopPartGraphic = ({
  selectedIntegration,
}: {
  selectedIntegration: FetchBiIntegrationApi | undefined;
}) => (
  <IconsWrapper>
    <LogoWrapper>
      <Logo />
    </LogoWrapper>

    <DeleteConnection />

    <PlatformIconWrapper>
      {selectedIntegration?.bi_type &&
        platformIconMap[selectedIntegration.bi_type]}
    </PlatformIconWrapper>
  </IconsWrapper>
);

type Props = {
  open: boolean;
  onClose: () => void;
  onSubmit: (id: string) => void;
  loading?: boolean;
  selectedIntegration?: FetchBiIntegrationApi;
};

function DeleteBiToolDialog({
  open,
  onClose,
  selectedIntegration,
  onSubmit,
  loading,
}: Props) {
  return (
    <Dialog
      open={open}
      graphic={TopPartGraphic({ selectedIntegration })}
      titleIcon={<DisabledIcon />}
      title={'Delete connection'}
      message={
        <Typography>
          Are you sure you want to delete your{' '}
          <Typography
            component="span"
            sx={{ mi: 0.5, fontWeight: fontWeight('semibold') }}
          >
            {selectedIntegration?.bi_type}
          </Typography>{' '}
          connection?
        </Typography>
      }
      secondaryMessage={`Deleting this connection will remove all your "${selectedIntegration?.name}" BI assets from Elementary. This action is irreversible.`}
      onClose={onClose}
      onConfirmButtonClick={() =>
        selectedIntegration && onSubmit(selectedIntegration.id)
      }
      isLoading={!!loading}
      confirmText={loading ? 'Deleting' : 'Delete connection'}
      variant="error"
    />
  );
}

export default DeleteBiToolDialog;
